import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Mail, 
  Send, 
  Check, 
  Loader2,
  Eye,
  EyeOff,
  AlertCircle,
  UserPlus,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Email Configuration Component for Super Admin
 */
const EmailConfig = ({ token }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingInvite, setTestingInvite] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  // Form state
  const [apiKey, setApiKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('TinyBank');
  const [isEnabled, setIsEnabled] = useState(true);
  
  // Test invite state
  const [testInviteEmail, setTestInviteEmail] = useState('');
  const [testInviteType, setTestInviteType] = useState('co_parent');
  
  useEffect(() => {
    fetchConfig();
  }, [token]);
  
  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setSenderEmail(data.sender_email);
        setSenderName(data.sender_name);
        setIsEnabled(data.is_enabled);
      } else if (response.status === 404) {
        // No config set yet
        setConfig(null);
      }
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (!apiKey && !config) {
      toast.error('Please enter your Resend API key');
      return;
    }
    
    if (!senderEmail) {
      toast.error('Please enter a sender email');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/email-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: 'resend',
          api_key: apiKey || config?.api_key_masked, // Keep existing if not changed
          sender_email: senderEmail,
          sender_name: senderName,
          is_enabled: isEnabled
        })
      });
      
      if (response.ok) {
        toast.success('Email configuration saved!');
        setApiKey(''); // Clear the input
        fetchConfig();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await fetch(`${API_URL}/api/email-config/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Test email sent! Check your inbox.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };
  
  const handleTestInvite = async () => {
    if (!testInviteEmail || !testInviteEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setTestingInvite(true);
    try {
      const response = await fetch(
        `${API_URL}/api/email-config/test-invite?recipient_email=${encodeURIComponent(testInviteEmail)}&invite_type=${testInviteType}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success(`Test invite sent to ${testInviteEmail}!`);
        setTestInviteEmail('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test invite');
      }
    } catch (error) {
      toast.error('Failed to send test invite');
    } finally {
      setTestingInvite(false);
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="email-config-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-400" />
              Email Configuration
            </CardTitle>
            <CardDescription className="text-slate-400">
              Configure Resend for sending notification emails
            </CardDescription>
          </div>
          {config && (
            <Badge className={config.is_enabled ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'}>
              {config.is_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Status */}
        {config ? (
          <div className="p-4 rounded-lg bg-slate-700/50 border border-slate-600">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-green-400 font-medium">Email Configured</span>
            </div>
            <p className="text-slate-400 text-sm">
              Sender: {config.sender_name} &lt;{config.sender_email}&gt;
            </p>
            <p className="text-slate-500 text-xs mt-1">
              API Key: {config.api_key_masked}
            </p>
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-medium">Not Configured</span>
            </div>
            <p className="text-slate-400 text-sm">
              Set up your Resend API key to enable email notifications.
            </p>
          </div>
        )}
        
        {/* Configuration Form */}
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Resend API Key</Label>
            <div className="relative mt-1">
              <Input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={config ? 'Enter new key to change' : 'Enter your Resend API key'}
                className="bg-slate-700 border-slate-600 text-white pr-10"
                data-testid="email-api-key"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-slate-500 text-xs mt-1">
              Get your API key from{' '}
              <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">
                resend.com
              </a>
            </p>
          </div>
          
          <div>
            <Label className="text-slate-300">Sender Email</Label>
            <Input
              type="email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="noreply@yourdomain.com"
              className="bg-slate-700 border-slate-600 text-white mt-1"
              data-testid="email-sender"
            />
            <p className="text-slate-500 text-xs mt-1">
              Must be verified in your Resend account
            </p>
          </div>
          
          <div>
            <Label className="text-slate-300">Sender Name</Label>
            <Input
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="TinyBank"
              className="bg-slate-700 border-slate-600 text-white mt-1"
              data-testid="email-sender-name"
            />
          </div>
          
          <div className="flex items-center justify-between pt-2">
            <div>
              <Label className="text-slate-300">Enable Emails</Label>
              <p className="text-slate-500 text-xs">Send notification emails to users</p>
            </div>
            <Switch
              checked={isEnabled}
              onCheckedChange={setIsEnabled}
              data-testid="email-enabled-toggle"
            />
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 flex-1"
            data-testid="save-email-config"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Save Configuration
          </Button>
          
          {config && config.is_enabled && (
            <Button
              onClick={handleTest}
              disabled={testing}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="test-email"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Test
            </Button>
          )}
        </div>
        
        {/* Test Invite Section */}
        {config && config.is_enabled && (
          <div className="mt-6 pt-6 border-t border-slate-700">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-amber-400" />
              Test Invite Email
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Send a sample invite email to preview how it looks to recipients.
            </p>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Recipient Email</Label>
                <Input
                  type="email"
                  value={testInviteEmail}
                  onChange={(e) => setTestInviteEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="test-invite-email"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Invite Type</Label>
                <Select value={testInviteType} onValueChange={setTestInviteType}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="test-invite-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="co_parent" className="text-white">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-400" />
                        <span>Co-Parent Invite</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="child" className="text-white">
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-green-400" />
                        <span>Child Account Invite</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                onClick={handleTestInvite}
                disabled={testingInvite || !testInviteEmail}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="send-test-invite"
              >
                {testingInvite ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test Invite
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailConfig;
